import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { getRide, submitRideRating } from '../services/firebase';
import { ArrowLeft, Banknote, Check, ChevronRight, Clock, Copy, HelpCircle, MapPin, Share2, Star, CarTaxiFront } from '../components/ReceiptIcons';
import toast from 'react-hot-toast';
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { useLanguage } from '../i18n/LanguageContext';
import SupportModal from '../components/SupportModal';
import RatingSheet from '../components/RatingSheet';
import RideDetailsMap from '../components/RideDetailsMap';
import { hapticSelection } from '../utils/haptics';
import { initNativeChrome } from '../utils/nativeChrome';
import { registerAndroidBackHandler } from '../utils/androidBack';
import { validPoint } from '../utils/rideStaticMap';
import { formatDriverName } from '../utils/driverName';
import { shareTripText } from '../utils/support';
import { formatMoney, getFareSnapshot, getRideDistanceKm, getRideDurationMin, getFareDistanceKm } from '../utils/rideData';
import { fareRows, fareRowLabel } from '../utils/fareRows';
import './RideDetails.css';

export default function RideDetails() {
  const { rideId } = useParams();
  const navigate = useNavigate();
  const { rides, userProfile } = useApp();
  const { t, lang } = useLanguage();
  const [showSupport, setShowSupport] = useState(false);
  const [showRating, setShowRating] = useState(false);
  const [ratingBusy, setRatingBusy] = useState(false);
  const [submittedRating, setSubmittedRating] = useState(null);
  const [fetchAttempt, setFetchAttempt] = useState(0);
  const currentRideId = useRef(rideId);
  const ratingInFlight = useRef(false);
  currentRideId.current = rideId;

  useEffect(() => {
    currentRideId.current = rideId;
    setSubmittedRating(null);
    setShowRating(false);
    setShowSupport(false);
    return () => { currentRideId.current = null; };
  }, [rideId]);

  useEffect(() => registerAndroidBackHandler(() => {
    if (showRating) {
      if (!ratingBusy) setShowRating(false);
      return true;
    }
    if (showSupport) {
      setShowSupport(false);
      return true;
    }
    return false;
  }), [showRating, showSupport, ratingBusy]);

  useEffect(() => {
    if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'android') return undefined;
    let active = true;
    const apply = async () => {
      try {
        await StatusBar.setBackgroundColor({ color: '#ffffff' });
        if (active) await StatusBar.setStyle({ style: Style.Light });
      } catch { /* Status bar styling is optional on unsupported Android versions. */ }
      if (!active) await initNativeChrome();
    };
    apply();
    return () => { active = false; initNativeChrome(); };
  }, []);

  // Context now streams only the newest few rides. If this detail page is for
  // one that isn't in that set, fetch just that single doc (one read, on demand).
  const contextRide = rides.find((r) => r.id === rideId) || null;
  const hasContextRide = Boolean(contextRide);
  const [fetchResult, setFetchResult] = useState(null);
  const fetched = fetchResult?.id === rideId ? fetchResult : null;
  useEffect(() => {
    if (hasContextRide || !rideId) return undefined;
    let cancelled = false;
    let timeout;
    setFetchResult({ id: rideId, status: 'loading', ride: null });
    Promise.race([
      getRide(rideId),
      new Promise((_, reject) => { timeout = setTimeout(() => reject(new Error('Ride fetch timed out')), 15000); }),
    ])
      .then((r) => { if (!cancelled) setFetchResult({ id: rideId, status: r ? 'ready' : 'missing', ride: r || null }); })
      .catch(() => { if (!cancelled) setFetchResult({ id: rideId, status: 'error', ride: null }); })
      .finally(() => clearTimeout(timeout));
    return () => { cancelled = true; clearTimeout(timeout); };
  }, [rideId, hasContextRide, fetchAttempt]);
  const ride = contextRide || fetched?.ride;

  const formatDate = (ts) => {
    if (!ts) return '';
    let d = null;
    if (ts?.toDate && typeof ts.toDate === 'function') d = ts.toDate();
    else if (ts instanceof Date) d = ts;
    else if (typeof ts === 'number') d = new Date(ts);
    else if (ts?.seconds) d = new Date(ts.seconds * 1000);
    else if (typeof ts === 'string') d = new Date(ts);
    if (!d || Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString(lang === 'te' ? 'te-IN' : 'en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const executeRebook = () => {
    if (!ride) return;
    hapticSelection();
    navigate('/', {
      state: {
        rebookDrop: validPoint(ride.destination)
          ? {
              name: ride.destination.name || ride.destination.address,
              address: ride.destination.address || ride.destination.name,
              lat: Number(ride.destination.lat),
              lng: Number(ride.destination.lng),
            }
          : null,
        rebookPickup: validPoint(ride.pickup)
          ? {
              name: ride.pickup.name || ride.pickup.address,
              address: ride.pickup.address || ride.pickup.name,
              lat: Number(ride.pickup.lat),
              lng: Number(ride.pickup.lng),
            }
          : null,
      },
    });
  };

  // Replace, not push: the rides list is where Android back already goes (App.js), and
  // pushing here made the browser's Back return to the receipt just left.
  const goBack = () => {
    hapticSelection();
    navigate('/rides', { replace: true });
  };
  // One white header for every state (loading, not found, receipt): the phone's status
  // bar is made white above, so it must never sit over a green header.
  const receiptHeader = (
    <header className="topbar ride-receipt-topbar">
      <button type="button" className="ride-receipt-icon-button" onClick={goBack} aria-label={t('common.back')}>
        <ArrowLeft size={24} aria-hidden="true" />
      </button>
      <h1>{t('details.receiptTitle')}</h1>
    </header>
  );

  if (!ride) {
    const loading = !fetched || fetched.status === 'loading';
    const failed = fetched?.status === 'error';
    return (
      <div className="app ride-receipt-page">
        {receiptHeader}
        <main className="page-content ride-receipt-content">
          {loading ? <div className="ride-receipt-empty" role="status"><span className="ride-receipt-loading" aria-hidden="true" />{t('details.rideLoading')}</div> : (
            <div className="ride-receipt-empty">
              <p>{failed ? t('details.rideLoadFailed') : t('active.rideNotFound')}</p>
              <button type="button" className="ride-receipt-book-button" onClick={failed ? () => setFetchAttempt((attempt) => attempt + 1) : goBack}>
                {failed ? t('details.retry') : t('history.doneToRides')}
              </button>
            </div>
          )}
        </main>
      </div>
    );
  }

  const isCompleted = ride.status === 'completed';
  // Nothing is charged on a cancelled ride's own record (a rider cancellation fee is
  // added to the NEXT booking), so its receipt shows no amount, payment or fare rows.
  const isCancelled = ride.status === 'cancelled';
  // First 8 characters, like the support sheet, the history modal and the admin screens,
  // so the ID a rider reads out matches the one support sees.
  const shortId = (ride.id || '').slice(0, 8).toUpperCase();
  const dateText = formatDate(ride.createdAt);
  const fareSnapshot = getFareSnapshot(ride);
  const distanceKm = getRideDistanceKm(ride);
  const fareDistanceKm = getFareDistanceKm(ride) ?? distanceKm;
  const durationMin = getRideDurationMin(ride);
  const calculationKm = fareDistanceKm != null ? Number(fareDistanceKm.toFixed(3)) : null;
  const cashPayment = !ride.paymentMode || ride.paymentMode === 'cash';
  const collected = ride.paymentStatus === 'collected';
  const paymentLabel = collected
    ? t(cashPayment ? 'details.paidInCash' : 'details.paymentReceived')
    : t(isCompleted || !cashPayment ? 'details.paymentPending' : 'details.cashDue');
  const canRebook = isCompleted && validPoint(ride.destination);
  const rating = submittedRating ?? ride.rating;
  const handleRatingSubmit = async (feedback) => {
    if (ratingInFlight.current) return;
    ratingInFlight.current = true;
    const ratedRideId = ride.id;
    setRatingBusy(true);
    try {
      await submitRideRating(ride.id, feedback);
      if (currentRideId.current !== ratedRideId) return;
      setSubmittedRating(feedback.rating);
      setShowRating(false);
      toast.success(t('rating.thankYou'));
    } catch {
      if (currentRideId.current === ratedRideId) toast.error(t('authErrors.default'));
    } finally {
      ratingInFlight.current = false;
      if (currentRideId.current) setRatingBusy(false);
    }
  };
  const copyRideId = async () => {
    try {
      await navigator.clipboard.writeText(ride.id);
      hapticSelection();
      toast.success(t('details.idCopied'));
    } catch {
      toast.error(t('details.copyFailed'));
    }
  };
  const shareReceipt = async () => {
    const lines = [`MyTownly Rides receipt #${shortId}`];
    if (dateText) lines.push(dateText);
    lines.push(`${ride.pickup?.address || ride.pickup?.name || 'Pickup'} → ${ride.destination?.address || ride.destination?.name || 'Destination'}`);
    if (ride.driverName) {
      lines.push(`Driver: ${formatDriverName(ride.driverName)}${ride.vehicleNumber ? ` · ${ride.vehicleNumber}` : ''}`);
    }
    if (isCancelled) {
      lines.push('Status: Cancelled');
    } else {
      lines.push(`Fare: ${formatMoney(fareSnapshot?.total ?? ride.fare)} (${ride.paymentMode === 'cash' ? 'Cash' : ride.paymentMode || 'Payment'})`);
      if (fareSnapshot?.waitingCharge > 0) lines.push(`Includes waiting charge: ${formatMoney(fareSnapshot.waitingCharge)} (${fareSnapshot.waitingMinutes} min)`);
      if (distanceKm != null) lines.push(`Distance: ${distanceKm.toFixed(1)} km`);
      if (durationMin != null) lines.push(`Duration: ${durationMin} min`);
    }
    const text = lines.join('\n');
    await shareTripText(text, 'MyTownly ride receipt');
  };

  return (
    <div className={`app ride-receipt-page${canRebook ? ' ride-receipt-with-footer' : ''}`}>
      {receiptHeader}

      <main className="page-content ride-receipt-content">
        <section className="ride-receipt-summary">
          <div className="ride-receipt-summary-heading">
            <h2>{t('details.receiptAutoRide')}</h2>
            <span className={`ride-receipt-status status-${ride.status}`}>
              {isCompleted && <span className="ride-receipt-check"><Check size={12} aria-hidden="true" /></span>}
              {t(`status.${ride.status}`)}
            </span>
          </div>
          {dateText && <p className="ride-receipt-date">{dateText}</p>}
          {!isCancelled && <>
            <div className="ride-receipt-amount">{formatMoney(fareSnapshot?.total ?? ride.fare)}</div>
            <p className="ride-receipt-payment-label">{paymentLabel}</p>
          </>}
        </section>

        <RideDetailsMap pickup={ride.pickup} destination={ride.destination} t={t} />

        <section className={`ride-receipt-route${isCancelled ? ' ride-receipt-route-end' : ''}`} aria-label={t('details.addresses')}>
          <div className="ride-receipt-stops">
            {[{ point: ride.pickup, label: t('book.pickup'), kind: 'pickup' },
              { point: ride.destination, label: t('details.dropoff'), kind: 'drop' }].map(({ point, label, kind }) => (
              <div className={`ride-receipt-stop ride-receipt-stop-${kind}`} key={kind}>
                <span className="ride-receipt-stop-dot" aria-hidden="true" />
                <div>
                  <span className="ride-receipt-stop-label">{label}</span>
                  <strong>{point?.name && point.name !== label ? point.name : point?.address || t('details.unavailable')}</strong>
                  {point?.address && point.address !== point.name && point?.name && point.name !== label && <p>{point.address}</p>}
                </div>
              </div>
            ))}
          </div>
          {!isCancelled && <div className="ride-receipt-metadata">
            <span><MapPin size={21} aria-hidden="true" />{fareDistanceKm != null ? `${fareDistanceKm.toFixed(1)} ${t('details.receiptKm')}` : t('details.unavailable')}</span>
            <span><Clock size={21} aria-hidden="true" />{durationMin != null ? `${durationMin} ${t('details.receiptMin')}` : t('details.unavailable')}</span>
          </div>}
          {!isCompleted && <button type="button" className="ride-receipt-action ride-receipt-quick-help" onClick={() => setShowSupport(true)}>
            <HelpCircle size={22} aria-hidden="true" /><span>{t('details.needHelp')}</span><ChevronRight size={20} aria-hidden="true" />
          </button>}
        </section>

        {(ride.driverName || ride.vehicleNumber || ride.driverRatingAvg) && <section className="ride-receipt-driver" aria-label={t('details.driverCard')}>
          <div className="ride-receipt-avatar">
            {ride.driverName ? ride.driverName.replace(/^(Anna|Captain)\s+/i, '').trim().charAt(0).toUpperCase() : 'A'}
          </div>
          <div className="ride-receipt-driver-info">
            <strong>{formatDriverName(ride.driverName)}</strong>
            <p>{t('driver.autoFallback')}{ride.vehicleNumber && ` · ${ride.vehicleNumber}`}</p>
            {Number(ride.driverRatingAvg) > 0 && <p className="ride-receipt-driver-rating"><Star size={13} aria-hidden="true" />{Number(ride.driverRatingAvg).toFixed(1)} · {t('driver.verified')}</p>}
          </div>
          {isCompleted && (rating ? <span className="ride-receipt-your-rating" aria-label={t('details.yourRating', { rating })}><Star size={22} aria-hidden="true" fill="currentColor" />{rating}</span> :
            <button type="button" className="ride-receipt-icon-button" title={t('rating.rateTrip')} aria-label={t('rating.rateTrip')} onClick={() => { hapticSelection(); setShowRating(true); }}><Star size={25} aria-hidden="true" /></button>)}
        </section>}

        {!isCancelled && <section className="ride-receipt-fare" aria-labelledby="ride-receipt-fare-title">
          <h2 id="ride-receipt-fare-title">{t('details.receiptFareBreakdown')}</h2>
          <div className="ride-receipt-fare-rows">
            {fareSnapshot && fareRows(fareSnapshot).rows.map((row) => (
              <div className="ride-receipt-fare-row" key={row.id}>
                <div>
                  <span>{fareRowLabel(row, t, { base: 'book.fareBase', distance: 'details.distanceFare' })}</span>
                  {row.id === 'distance' && calculationKm != null && fareSnapshot.perKm != null && <p>{Math.round(calculationKm * fareSnapshot.perKm) === row.amount
                    ? <>{t('details.distanceCalculation', { km: calculationKm, rate: fareSnapshot.perKm })}{Math.abs(calculationKm * fareSnapshot.perKm - row.amount) > 0.001 ? ` · ${t('details.rounded')}` : ''}</>
                    : t('details.distanceRate', { rate: fareSnapshot.perKm })}</p>}
                </div>
                <span>{formatMoney(row.amount)}</span>
              </div>
            ))}
            <div className="ride-receipt-fare-row ride-receipt-total">
              <strong>{t(collected ? 'details.totalPaid' : 'fare.total')}</strong>
              <strong>{formatMoney(fareSnapshot?.total ?? ride.fare)}</strong>
            </div>
          </div>
          <div className="ride-receipt-payment">
            <span><Banknote size={24} aria-hidden="true" />{cashPayment ? t('pay.cash') : ride.paymentMode}</span>
            <span className={collected ? 'ride-receipt-paid' : 'ride-receipt-pending'}>{collected && <span className="ride-receipt-check"><Check size={15} aria-hidden="true" /></span>}{t(collected ? 'details.paid' : 'details.paymentPending')}</span>
          </div>
          {!fareSnapshot && <p className="ride-receipt-fare-note">{t('details.fareBreakdownUnavailable')}</p>}
        </section>}

        <section className="ride-receipt-actions" aria-label={t('details.receiptActions')}>
          <button type="button" className="ride-receipt-action" onClick={shareReceipt}><Share2 size={23} aria-hidden="true" /><span>{t('details.shareReceipt')}</span><ChevronRight size={21} aria-hidden="true" /></button>
          <button type="button" className="ride-receipt-action" onClick={() => setShowSupport(true)}><HelpCircle size={23} aria-hidden="true" /><span>{t('details.reportIssue')}</span><ChevronRight size={21} aria-hidden="true" /></button>
        </section>
        <div className="ride-receipt-reference">
          <span><strong>{t('details.rideId')}</strong> #{shortId}</span>
          <button type="button" className="ride-receipt-icon-button" aria-label={t('details.copyId')} title={t('details.copyId')} onClick={copyRideId}><Copy size={20} aria-hidden="true" /></button>
        </div>
      </main>

      {canRebook && (
        <footer className="ride-receipt-footer">
          <button type="button" className="ride-receipt-book-button" onClick={executeRebook}>
            <CarTaxiFront size={22} aria-hidden="true" />{t('details.receiptRebook')}
          </button>
        </footer>
      )}

      {showRating && <RatingSheet t={t} fare={fareSnapshot?.total ?? ride.fare} busy={ratingBusy} onSubmit={handleRatingSubmit} onSkip={() => setShowRating(false)} />}

      {showSupport && (
        <SupportModal
          open={showSupport}
          onClose={() => setShowSupport(false)}
          defaultRide={ride}
          userProfile={userProfile}
          t={t}
        />
      )}
    </div>
  );
}
